/**
 * Document parse & create-shipment API client.
 * 문서 업로드/파싱 요청은 EC2로 전송됩니다.
 *
 * Backend (EC2): POST /api/document/parse, POST /api/document/create-shipment
 *
 * Base URL: REACT_APP_DOCUMENT_API (EC2 주소, 예: http://ec2-xxx.compute.amazonaws.com:3002)
 *           → .env 에서 소켓과 동일 포트(3002) 사용
 */
import axios from 'axios';

const auth0API = process.env.REACT_APP_AUTH0_AUTHORIZATION_API;

/** 문서 API는 EC2로 요청. REACT_APP_DOCUMENT_API 미설정 시 DEFAULT_API 사용(같은 Gateway에 EC2 통합한 경우) */
export const getDocumentApiBase = () => {
    const base = process.env.REACT_APP_DOCUMENT_API || process.env.REACT_APP_DEFAULT_API || '';
    return base.replace(/\/$/, ''); // trailing slash 제거
};

export const DOCUMENT_STATUS = {
    PENDING: 'pending',
    PARSING: 'parsing',
    SUCCESS: 'success',
    FAILED: 'failed',
};

/**
 * Parse a single PDF and optionally create shipment.
 * @param {Object} params
 * @param {File} params.file - PDF file
 * @param {number} params.shipmentType - 0=OIM, 1=OEX, 2=AIM, 3=AEX, 4=Trucking
 * @param {boolean} params.autoCreate - if true, save and emit; if false, return parsed_data for preview
 * @param {number} [params.clientId] - required when autoCreate=true
 * @param {string} [params.sid] - socket id for real-time emit
 * @param {function} params.getAccessTokenSilently - Auth0
 * @param {function} [params.onUploadProgress] - (percent) => {}
 * @param {AbortSignal} [params.signal] - aborts the request when the caller goes away (e.g. dialog closed mid-parse)
 * @returns {Promise<{ status: number, message?: string, parsed_data?: object, location_matches?: array, partner_matches?: array }>}
 */
export async function parseDocument({
    file,
    shipmentType = 0,
    autoCreate = false,
    clientId,
    sid = '',
    getAccessTokenSilently,
    onUploadProgress,
    signal,
}) {
    const token = await getAccessTokenSilently({
        authorizationParams: { audience: auth0API },
    });
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('shipment_type', String(shipmentType + 1)); // backend: 1=OIM, 2=OEX, ...
    form.append('auto_create', String(autoCreate));
    if (clientId != null) form.append('client_id', String(clientId));
    if (sid) form.append('sid', sid);

    const base = getDocumentApiBase();
    const resp = await axios.post(`${base}/api/document/parse`, form, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
        },
        signal,
        onUploadProgress: onUploadProgress
            ? (e) => {
                  const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
                  onUploadProgress(pct);
              }
            : undefined,
    });
    return resp.data;
}

/**
 * Create shipment from preview/parsed data (with optional overrides).
 * @param {Object} params
 * @param {object} params.parsedData - parsed_data from parse or edited preview
 * @param {number} params.shipmentType - 0=OIM, 1=OEX, 2=AIM, 3=AEX, 4=Trucking
 * @param {number} params.clientId - client (trade partner) id
 * @param {object} [params.overrides] - user-edited fields
 * @param {string} [params.sid] - socket id
 * @param {function} params.getAccessTokenSilently - Auth0
 * @returns {Promise<{ status: number, message?: string, data?: object }>}
 */
export async function createShipmentFromDocument({
    parsedData,
    shipmentType = 0,
    clientId,
    overrides = {},
    sid = '',
    getAccessTokenSilently,
}) {
    const token = await getAccessTokenSilently({
        authorizationParams: { audience: auth0API },
    });
    const base = getDocumentApiBase();
    const resp = await axios.post(
        `${base}/api/document/create-shipment`,
        {
            parsed_data: parsedData,
            shipment_type: shipmentType + 1, // backend 1-based
            client_id: clientId,
            overrides,
            sid,
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        }
    );
    return resp.data;
}
